import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { useSearchParams } from "react-router-dom";
import { Plus, Send, Pencil, Trash2, X, Mail, CheckCircle2, ArrowUpDown, CalendarClock, Circle, TestTube2, BellRing } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import ColumnOrderMenu from "../../components/ColumnOrderMenu";
import useColumnPrefs from "../../lib/useColumnPrefs";

const money = (v) => `£${(Number(v) || 0).toFixed(2)}`;

// Column registry for the Invoices desktop table. Per-admin reorder/hide
// preferences save to Mongo via /api/admin/me/prefs. "number" and "actions"
// stay pinned to the ends (locked).
const INVOICE_COLUMNS_META = [
  { id: "number", label: "Invoice No.", locked: true },
  { id: "student", label: "Student" },
  { id: "term", label: "Term" },
  { id: "total", label: "Total" },
  { id: "paid", label: "Paid" },
  { id: "balance", label: "Balance" },
  { id: "due", label: "Due date" },
  { id: "reminder", label: "Last reminder" },
  { id: "status", label: "Status" },
  { id: "actions", label: "Quick actions", locked: true },
];

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [students, setStudents] = useState([]);
  const [addons, setAddons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null); // { invoice, value }
  const [sortBy, setSortBy] = useState("issued_at");
  const [sortDir, setSortDir] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("");
  const [termFilter, setTermFilter] = useState("");
  const [studentFilter, setStudentFilter] = useState("");  // deep-link ?student=<id> from Students page
  const [colPrefs, setColPrefs] = useColumnPrefs("invoices", { order: [], hidden: [] });
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link support: dashboard "By term" tiles link here with ?term=Autumn.
  // Students-page "Payment" cell links with ?student=<id>.
  useEffect(() => {
    const t = searchParams.get("term");
    const sid = searchParams.get("student");
    if (t) setTermFilter(t);
    if (sid) setStudentFilter(sid);
    if (t || sid) setSearchParams({}, { replace: true });
    // eslint-disable-next-line
  }, []);

  const load = useCallback(async () => {
    // Guard against 403 for staff without the "settings" perm — the page
    // works fine without addons (they just won't see them in the picker).
    const [i, s, st] = await Promise.all([
      api.get("/admin/invoices").catch(() => ({ data: [] })),
      api.get("/admin/students").catch(() => ({ data: [] })),
      api.get("/admin/settings").catch(() => ({ data: {} })),
    ]);
    setInvoices(i.data); setStudents(s.data);
    setAddons(st.data.invoice_addons || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (data) => {
    try { await api.post("/admin/invoices", data); toast.success("Invoice created"); setShowForm(false); await load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => { if (!confirm("Delete this invoice?")) return; await api.delete(`/admin/invoices/${id}`); toast.success("Deleted"); load(); };

  const send = async (inv, emailOverride, testMode = false) => {
    try {
      const body = {};
      if (testMode) {
        body.test_to = user?.email;
      } else if (emailOverride) {
        body.email = emailOverride;
      }
      await api.post(`/admin/invoices/${inv.id}/send`, body);
      toast.success(testMode ? `Test sent to ${user?.email} ✓` : "Invoice emailed ✓");
      setSendingEmail(null);
      if (!testMode) await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Send failed";
      if (!testMode && msg.toLowerCase().includes("no email")) {
        setSendingEmail({ invoice: inv, value: inv.student_email || "" });
      } else {
        toast.error(msg);
      }
    }
  };

  const markPaid = async (inv) => {
    const outstanding = Math.max(0, (inv.total ?? inv.amount ?? 0) - (inv.amount_paid ?? 0));
    const money = `£${outstanding.toFixed(2)}`;
    if (!confirm(`Record a ${money} payment for invoice ${inv.invoice_number} and email a receipt to ${inv.student_email || "the student"}?`)) return;
    try {
      const { data } = await api.post(`/admin/invoices/${inv.id}/mark-paid`);
      const recorded = data?.payment_recorded;
      toast.success(recorded ? `Marked paid · £${(data?.payment_amount || 0).toFixed(2)} logged + receipt emailed ✓` : "Already paid in full");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendReminder = async (inv) => {
    // Manual chase — same template the daily scheduler uses (auto-derived
    // bank block + next-instalment awareness), delivered in the background
    // so the click returns in <300 ms. `reminder_last_sent_at` is stamped
    // by the backend once SMTP acks; a follow-up load() refreshes the
    // Last Sent column so both auto + manual sends show in the same place.
    if (!inv.student_email) { toast.error("This invoice has no email on file"); return; }
    if (!confirm(`Send a payment reminder to ${inv.student_name} (${inv.student_email}) for invoice ${inv.invoice_number}?`)) return;
    try {
      await api.post(`/admin/invoices/${inv.id}/send-reminder`);
      toast.success("Reminder queued for delivery ✓");
      // Small delay so the background stamp lands before we refetch
      setTimeout(() => { load(); }, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reminder failed"); }
  };

  const backfillPaidReceipts = async () => {
    if (!confirm("Repair historic invoices?\n\nThis scans every status='paid' invoice that has no payment record (legacy mark-paid clicks) and injects the missing payment. No emails are sent. Safe to run multiple times.")) return;
    try {
      const { data } = await api.post("/admin/invoices/backfill-paid-receipts");
      if (data.fixed === 0) toast.success(`No broken invoices found (scanned ${data.scanned})`);
      else toast.success(`Repaired ${data.fixed} invoice${data.fixed === 1 ? "" : "s"} · revenue stats now accurate ✓`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
  };

  // Filter + sort the invoices list
  const visible = useMemo(() => {
    let arr = invoices.filter((i) => {
      if (statusFilter && i.status !== statusFilter) return false;
      if (termFilter && (i.term || "") !== termFilter) return false;
      if (studentFilter && i.student_id !== studentFilter) return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    const get = (i) => {
      switch (sortBy) {
        case "student": return (i.student_name || "").toLowerCase();
        case "total": return Number(i.total ?? i.amount ?? 0);
        case "balance": return Number(i.balance_due ?? Math.max(0, (i.total ?? i.amount ?? 0) - (i.amount_paid ?? 0)));
        case "due_date": return i.due_date || "";
        case "status": return i.status || "";
        case "term": return i.term || "";
        default: return i.issued_at || "";
      }
    };
    return [...arr].sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [invoices, sortBy, sortDir, statusFilter, termFilter, studentFilter]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir(key === "issued_at" ? "desc" : "asc"); }
  };

  const terms = useMemo(() => Array.from(new Set(invoices.map((i) => i.term).filter(Boolean))).sort().reverse(), [invoices]);

  return (
    <div data-testid="admin-invoices-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">payments & invoices</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Invoices</h1>
          <p className="text-sm text-[#6E6074] mt-1">Add line items (starter pack, paper pack), log part-payments like deposits, set a balance-due date and apply discounts — just like a proper invoice.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={backfillPaidReceipts} className="sac-btn-secondary text-xs" data-testid="invoices-backfill-btn" title="One-off: repair historic paid invoices that were missing a payment record (caused inaccurate revenue stats)">Repair revenue data</button>
          <button onClick={() => setShowForm(true)} className="sac-btn-primary" data-testid="invoices-add-btn"><Plus size={16} /> Create invoice</button>
        </div>
      </div>

      {/* Filters + sort controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="invoices-status-filter"
          className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm">
          <option value="">All statuses</option>
          <option value="draft">draft</option>
          <option value="sent">sent</option>
          <option value="partial">partial</option>
          <option value="paid">paid</option>
          <option value="cancelled">cancelled</option>
        </select>
        <select value={termFilter} onChange={(e) => setTermFilter(e.target.value)}
          data-testid="invoices-term-filter"
          className="bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm">
          <option value="">All terms</option>
          {terms.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {(statusFilter || termFilter || studentFilter) && (
          <button onClick={() => { setStatusFilter(""); setTermFilter(""); setStudentFilter(""); }} className="text-xs text-[#A685BD] hover:underline" data-testid="invoices-clear-filters">Clear filters</button>
        )}
        {studentFilter && (
          <span className="text-xs px-3 py-1 rounded-full bg-[#5D2C8F] text-white inline-flex items-center gap-2" data-testid="invoices-student-filter-pill">
            Filtered to {students.find((s) => s.id === studentFilter)?.name || "student"}
            <button onClick={() => setStudentFilter("")} className="text-white/70 hover:text-white" aria-label="Clear student filter">×</button>
          </span>
        )}
        <ColumnOrderMenu
          columns={INVOICE_COLUMNS_META}
          value={colPrefs}
          onChange={setColPrefs}
          testid="invoices-columns-menu"
        />
        <span className="ml-auto text-sm text-[#6E6074]">{visible.length} invoice{visible.length === 1 ? "" : "s"}</span>
      </div>

      {/* Mobile card view (phones <768px) */}
      <div className="md:hidden space-y-3" data-testid="invoices-mobile-cards">
        {visible.map((i) => {
          const total = i.total ?? i.amount;
          const paid = i.amount_paid ?? 0;
          const balance = i.balance_due ?? Math.max(0, total - paid);
          const statusClass = i.status === 'paid'
            ? 'bg-[#8EC9C7]/20 text-[#5D2C8F]'
            : i.status === 'partial' ? 'bg-[#E8C37A]/20 text-[#8F6B1F]'
            : i.status === 'sent' ? 'bg-[#E8C37A]/15 text-[#8F6B1F]'
            : 'bg-[#E5E0D8] text-[#6E6074]';
          return (
            <div key={i.id} className="admin-surface p-4" data-testid={`invoice-card-${i.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-[#A685BD]">{i.invoice_number}</div>
                  <div className="font-medium text-[#5D2C8F] truncate">{i.student_name}</div>
                  <div className="text-xs text-[#6E6074] truncate">{i.student_email || <em>no email</em>}</div>
                </div>
                <span className={`text-[11px] px-2 py-1 rounded-full shrink-0 ${statusClass}`}>{i.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 my-3 text-center">
                <div className="p-2 rounded bg-[#FAF6EE]">
                  <div className="text-[10px] uppercase tracking-wide text-[#8A7C92]">Total</div>
                  <div className="text-sm font-medium text-[#5D2C8F]">{money(total)}</div>
                  {i.discount > 0 && <div className="text-[9px] text-[#C48A2B]">−{money(i.discount)}</div>}
                </div>
                <div className="p-2 rounded bg-[#FAF6EE]">
                  <div className="text-[10px] uppercase tracking-wide text-[#8A7C92]">Paid</div>
                  <div className="text-sm font-medium text-[#348469]">{money(paid)}</div>
                </div>
                <div className="p-2 rounded bg-[#FAF6EE]">
                  <div className="text-[10px] uppercase tracking-wide text-[#8A7C92]">Balance</div>
                  <div className="text-sm font-semibold text-[#5D2C8F]">{money(balance)}</div>
                </div>
              </div>
              {i.due_date && <div className="text-xs text-[#6E6074] mb-3">Due: {i.due_date}</div>}
              <div className="grid grid-cols-4 gap-2 pt-2 border-t border-[#E5DCE6]">
                {i.status !== 'paid'
                  ? <button onClick={() => send(i)} className="bg-[#FAF6EE] text-[#5D2C8F] rounded px-2 py-2 text-xs flex items-center justify-center gap-1"><Send size={12} /> Send</button>
                  : <div />}
                {i.status !== 'paid' && Number(i.balance_due ?? 0) > 0.01
                  ? <button onClick={() => sendReminder(i)} className="bg-[#FAF6EE] text-[#C48A2B] rounded px-2 py-2 text-xs flex items-center justify-center gap-1" data-testid={`m-invoice-send-reminder-${i.id}`}><BellRing size={12} /> Remind</button>
                  : <div />}
                {i.status !== 'paid'
                  ? <button onClick={() => markPaid(i)} className="bg-[#FAF6EE] text-[#348469] rounded px-2 py-2 text-xs flex items-center justify-center gap-1" data-testid={`m-invoice-mark-paid-${i.id}`}><CheckCircle2 size={12} /> Paid</button>
                  : <div />}
                <button onClick={() => setEditing(i)} className="bg-[#FAF6EE] text-[#5D2C8F] rounded px-2 py-2 text-xs flex items-center justify-center gap-1"><Pencil size={12} /> Edit</button>
              </div>
              {i.reminder_last_sent_at && (
                <div className="text-[10px] text-[#8A7C92] mt-2 flex items-center gap-1" data-testid={`m-invoice-last-reminder-${i.id}`}>
                  <BellRing size={9} /> Last reminder: {new Date(i.reminder_last_sent_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                </div>
              )}
            </div>
          );
        })}
        {visible.length === 0 && <div className="admin-surface p-10 text-center text-[#6E6074] text-sm">No invoices yet.</div>}
      </div>

      {/* Desktop + tablet table */}
      <div className="hidden md:block admin-surface overflow-x-auto">
        <table className="w-full text-sm min-w-[1050px]">
          <InvoicesTableBody
            rows={visible}
            colPrefs={colPrefs}
            sortBy={sortBy}
            sortDir={sortDir}
            toggleSort={toggleSort}
            handlers={{ send, markPaid, sendReminder, setEditing, remove }}
          />
        </table>
      </div>

      {showForm && <InvoiceForm students={students} onClose={() => setShowForm(false)} onCreate={create} />}
      {editing && <InvoiceEditor invoice={editing} addons={addons} onClose={() => setEditing(null)} onChanged={load} />}
      {sendingEmail && (
        <AskEmailModal
          value={sendingEmail.value}
          onChange={(v) => setSendingEmail({ ...sendingEmail, value: v })}
          onCancel={() => setSendingEmail(null)}
          onSubmit={() => send(sendingEmail.invoice, sendingEmail.value)}
        />
      )}
    </div>
  );
}

function AskEmailModal({ value, onChange, onCancel, onSubmit }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-6" onClick={onCancel}>
      <div className="admin-surface w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="invoice-ask-email-modal">
        <h3 className="font-display text-xl text-[#5D2C8F] mb-1 flex items-center gap-2"><Mail size={18} /> Email address needed</h3>
        <p className="text-sm text-[#6E6074] mb-4">This student doesn't have an email on file. Enter one to send the invoice — we'll also save it to their record.</p>
        <input type="email" value={value} onChange={(e) => onChange(e.target.value)} placeholder="student@example.com" autoFocus
          className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="invoice-ask-email-input" />
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onCancel} className="text-sm text-[#6E6074]">Cancel</button>
          <button onClick={onSubmit} disabled={!/\S+@\S+\.\S+/.test(value)} className="sac-btn-primary disabled:opacity-50" data-testid="invoice-ask-email-submit"><Send size={14} /> Send invoice</button>
        </div>
      </div>
    </div>
  );
}

function InvoiceEditor({ invoice, addons = [], onClose, onChanged }) {
  const [inv, setInv] = useState(invoice);
  const [pmt, setPmt] = useState({ amount: "", method: "bank_transfer", date: new Date().toISOString().slice(0, 10), note: "" });

  const recompute = (items, discount) => {
    const subtotal = items.reduce((a, it) => a + (Number(it.quantity || 0) * Number(it.unit_price || 0)), 0);
    const total = Math.max(0, +(subtotal - Number(discount || 0)).toFixed(2));
    return { subtotal, total };
  };
  const { subtotal, total } = useMemo(() => recompute(inv.items || [], inv.discount || 0), [inv.items, inv.discount]);
  const paid = (inv.payments || []).reduce((a, p) => a + Number(p.amount || 0), 0);
  const balance = Math.max(0, +(total - paid).toFixed(2));

  const setField = (k, v) => setInv((s) => ({ ...s, [k]: v }));
  const setItem = (idx, k, v) => setInv((s) => ({ ...s, items: s.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));
  const addItem = (preset) => setInv((s) => {
    const _key = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `it_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    return { ...s, items: [...(s.items || []), { _key, ...(preset || { description: "", quantity: 1, unit_price: 0 }) }] };
  });
  const removeItem = (idx) => setInv((s) => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (plan.length > 0 && !planValid) {
      toast.error(`Payment plan adds up to £${planTotal.toFixed(2)} but invoice total is £${total.toFixed(2)}`);
      return;
    }
    try {
      const items = inv.items.map(it => ({ ...it, quantity: Number(it.quantity || 1), unit_price: Number(it.unit_price || 0) }));
      await api.patch(`/admin/invoices/${inv.id}`, {
        items, discount: Number(inv.discount || 0), notes: inv.notes, status: inv.status,
        method: inv.method, due_date: inv.due_date || null, student_email: inv.student_email,
        payment_plan: plan.length === 0 ? null : plan.map(({ _k, ...p }) => ({ ...p, amount: Number(p.amount || 0) })),
      });
      toast.success("Invoice saved");
      onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const addPayment = async () => {
    if (!pmt.amount || Number(pmt.amount) <= 0) { toast.error("Enter an amount"); return; }
    try {
      // P0 fix: persist any unsaved edits (line items, discount, due_date, etc.)
      // BEFORE recording the payment, otherwise the backend will recompute the
      // invoice from the stale DB version and silently wipe the user's additions.
      const items = (inv.items || []).map(it => ({ ...it, quantity: Number(it.quantity || 1), unit_price: Number(it.unit_price || 0) }));
      await api.patch(`/admin/invoices/${inv.id}`, {
        items, discount: Number(inv.discount || 0), notes: inv.notes,
        method: inv.method, due_date: inv.due_date || null, student_email: inv.student_email,
        payment_plan: plan.length === 0 ? null : plan.map(({ _k, ...p }) => ({ ...p, amount: Number(p.amount || 0) })),
      });
      const { data } = await api.post(`/admin/invoices/${inv.id}/add-payment`, { ...pmt, amount: Number(pmt.amount) });
      setInv(data);
      setPmt({ amount: "", method: "bank_transfer", date: new Date().toISOString().slice(0, 10), note: "" });
      toast.success(data.student_email ? "Payment recorded · receipt emailed ✓" : "Payment recorded (no email on file)");
      onChanged();
    } catch { toast.error("Failed"); }
  };
  const removePayment = async (pid) => {
    if (!confirm("Remove this payment?")) return;
    try { const { data } = await api.post(`/admin/invoices/${inv.id}/remove-payment`, { payment_id: pid }); setInv(data); onChanged(); }
    catch { toast.error("Failed"); }
  };

  // ----- Payment plan helpers -----
  const plan = inv.payment_plan || [];
  const planTotal = plan.reduce((a, p) => a + Number(p.amount || 0), 0);
  const planValid = plan.length === 0 || Math.abs(planTotal - total) < 0.01;
  const enablePlan = () => {
    const deposit = Math.round(total / 3 * 100) / 100;
    const remainder = +(total - deposit).toFixed(2);
    setField("payment_plan", [
      { _k: `pl-${Date.now()}-1`, amount: deposit, due_date: null, label: "Deposit (due immediately)" },
      { _k: `pl-${Date.now()}-2`, amount: remainder, due_date: null, label: "Balance" },
    ]);
  };
  const disablePlan = () => setField("payment_plan", null);
  const addInstalment = () => {
    if (plan.length >= 3) return;
    setField("payment_plan", [...plan, { _k: `pl-${Date.now()}-${plan.length + 1}`, amount: 0, due_date: "", label: `Instalment ${plan.length + 1}` }]);
  };
  const removeInstalment = (idx) => {
    if (idx === 0) return; // First instalment is fixed
    setField("payment_plan", plan.filter((_, i) => i !== idx));
  };
  const updateInstalment = (idx, field, value) => {
    const next = plan.map((p, i) => i === idx ? { ...p, [field]: value } : p);
    setField("payment_plan", next);
  };
  // Auto-balance: when 1st & 2nd are filled, top up the last to make total match.
  const balanceLast = () => {
    if (plan.length < 2) return;
    const lastIdx = plan.length - 1;
    const otherSum = plan.slice(0, lastIdx).reduce((a, p) => a + Number(p.amount || 0), 0);
    const lastAmt = +(total - otherSum).toFixed(2);
    if (lastAmt < 0) { toast.error("Earlier instalments already exceed the total"); return; }
    updateInstalment(lastIdx, "amount", lastAmt);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="admin-surface w-full max-w-3xl p-6 my-10" onClick={(e) => e.stopPropagation()} data-testid="invoice-editor">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs text-[#6E6074] font-mono">{inv.invoice_number}</div>
            <h2 className="font-display text-2xl text-[#5D2C8F]">{inv.student_name}</h2>
          </div>
          <button onClick={onClose} className="text-[#A685BD] p-1"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <Field label="Student email (editable)">
            <input type="email" value={inv.student_email || ""} onChange={(e) => setField("student_email", e.target.value)} placeholder="student@example.com" className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="invedit-email" />
          </Field>
          <Field label="Status">
            <select value={inv.status} onChange={(e) => setField("status", e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="invedit-status">
              {["draft", "sent", "partial", "paid", "cancelled"].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Method">
            <select value={inv.method} onChange={(e) => setField("method", e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="invedit-method">
              <option value="bank_transfer">Bank transfer (preferred)</option>
              <option value="stripe">Stripe (card)</option>
              <option value="cash">Cash</option>
            </select>
          </Field>
          <Field label="Balance due by">
            <input type="date" value={inv.due_date || ""} onChange={(e) => setField("due_date", e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="invedit-due-date" />
          </Field>
        </div>

        {/* Line items */}
        <div className="bg-[#FAF6EE] rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium text-[#5D2C8F]">Line items</div>
            <div className="flex gap-1 flex-wrap">
              {addons.map((a) => (
                <button key={a.description} onClick={() => addItem({ ...a, quantity: 1 })} className="text-[11px] px-2 py-1 rounded-full bg-white border border-[#E5E0D8] text-[#5D2C8F] hover:bg-[#E8C37A]/20" data-testid={`addon-${a.description.toLowerCase().replace(/\s/g, '-')}`}>
                  + {a.description} ({money(a.unit_price)})
                </button>
              ))}
              <button onClick={() => addItem()} className="text-[11px] px-2 py-1 rounded-full bg-[#5D2C8F] text-white" data-testid="addon-custom">+ Custom line</button>
            </div>
          </div>
          <table className="w-full text-xs">
            <thead className="text-[#5D2C8F]"><tr>
              <th className="text-left font-semibold pb-1">Description</th>
              <th className="font-semibold pb-1 w-16 text-right">Qty</th>
              <th className="font-semibold pb-1 w-24 text-right">Unit £</th>
              <th className="font-semibold pb-1 w-24 text-right">Total</th>
              <th></th>
            </tr></thead>
            <tbody>
              {(inv.items || []).map((it, idx) => {
                const total = Number(it.quantity || 0) * Number(it.unit_price || 0);
                return (
                  <tr key={it._key || `item-${idx}`} className="border-t border-[#E5E0D8]/60" data-testid={`invedit-item-${idx}`}>
                    <td className="py-1 pr-2"><input value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1" /></td>
                    <td className="py-1"><input type="number" min="0" step="1" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1 text-right" /></td>
                    <td className="py-1"><input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1 text-right" /></td>
                    <td className="py-1 text-right font-medium text-[#5D2C8F] pr-2">{money(total)}</td>
                    <td className="py-1 text-right"><button onClick={() => removeItem(idx)} className="text-[#A685BD]" data-testid={`invedit-item-del-${idx}`}><Trash2 size={12} /></button></td>
                  </tr>
                );
              })}
              {(!inv.items || inv.items.length === 0) && <tr><td colSpan={5} className="py-4 text-center text-[#6E6074]">No line items — add one above.</td></tr>}
            </tbody>
          </table>
          <div className="mt-3 ml-auto w-64 text-xs">
            <Row label="Subtotal" value={money(subtotal)} />
            <div className="flex items-center justify-end gap-2 py-0.5">
              <span className="text-[#7D6096]">Discount £</span>
              <input type="number" min="0" step="1" value={inv.discount || 0} onChange={(e) => setField("discount", Number(e.target.value || 0))} className="w-20 bg-white border border-[#E5E0D8] rounded px-2 py-1 text-right" data-testid="invedit-discount" />
            </div>
            <Row label="Total" value={money(total)} bold />
            <Row label="Paid" value={money(paid)} colour="#348469" />
            <Row label="Balance due" value={money(balance)} bold />
          </div>
        </div>

        {/* Payment plan */}
        <div className="bg-[#FAF6EE] rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium text-[#5D2C8F] flex items-center gap-2"><CalendarClock size={16} /> Payment plan {plan.length > 0 && <span className="text-xs text-[#6E6074]">({plan.length} instalment{plan.length === 1 ? "" : "s"})</span>}</div>
            {plan.length === 0 ? (
              <button onClick={enablePlan} className="sac-btn-secondary !py-1 text-xs" data-testid="plan-enable" disabled={total <= 0}><Plus size={11} /> Set up payment plan</button>
            ) : (
              <button onClick={disablePlan} className="text-xs text-[#A685BD] hover:underline" data-testid="plan-disable">Remove plan</button>
            )}
          </div>
          {plan.length === 0 ? (
            <div className="text-xs text-[#6E6074]">No payment plan — student pays the full balance by the &ldquo;Balance due by&rdquo; date above. Click <em>Set up payment plan</em> to split into up to 3 instalments.</div>
          ) : (
            <>
              <div className="space-y-2">
                {plan.map((p, idx) => {
                  const isFirst = idx === 0;
                  return (
                    <div key={p._k || `idx-${idx}`} className={`grid gap-2 items-end p-2 rounded-lg ${p.paid ? "bg-[#8EC9C7]/15" : "bg-white"} border border-[#E5E0D8]`} style={{ gridTemplateColumns: "auto 2fr 1fr 1fr auto" }} data-testid={`plan-row-${idx}`}>
                      <div className="text-[#5D2C8F]" title={p.paid ? "Paid" : "Outstanding"}>
                        {p.paid ? <CheckCircle2 size={16} className="text-[#348469]" data-testid={`plan-paid-${idx}`} /> : <Circle size={16} className="text-[#A685BD]" />}
                      </div>
                      <Field label={isFirst ? "Label · due immediately" : `Label · instalment ${idx + 1}`}>
                        <input value={p.label || ""} onChange={(e) => updateInstalment(idx, "label", e.target.value)} placeholder={isFirst ? "Deposit" : "Instalment"} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1.5 text-xs" data-testid={`plan-label-${idx}`} />
                      </Field>
                      <Field label="Due date">
                        {isFirst ? (
                          <div className="text-xs text-[#6E6074] py-1.5 italic">Immediately</div>
                        ) : (
                          <input type="date" value={p.due_date || ""} onChange={(e) => updateInstalment(idx, "due_date", e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1.5 text-xs" data-testid={`plan-due-${idx}`} />
                        )}
                      </Field>
                      <Field label="Amount £">
                        <input type="number" min="0" step="0.01" value={p.amount} onChange={(e) => updateInstalment(idx, "amount", e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1.5 text-xs" data-testid={`plan-amount-${idx}`} />
                      </Field>
                      {isFirst
                        ? <div />
                        : <button onClick={() => removeInstalment(idx)} className="text-[#A685BD] p-1.5" data-testid={`plan-remove-${idx}`}><Trash2 size={12} /></button>}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between gap-3 mt-3 text-xs">
                <div className={planValid ? "text-[#348469]" : "text-[#C9A24E]"} data-testid="plan-summary">
                  Scheduled: <strong>{money(planTotal)}</strong> / Total: <strong>{money(total)}</strong>{" "}
                  {planValid ? <CheckCircle2 size={11} className="inline ml-1" /> : <span className="font-semibold">— off by {money(Math.abs(planTotal - total))}</span>}
                </div>
                <div className="flex gap-2">
                  {!planValid && <button onClick={balanceLast} className="text-[#5D2C8F] underline" data-testid="plan-balance">Auto-balance last</button>}
                  {plan.length < 3 && <button onClick={addInstalment} className="sac-btn-secondary !py-1 text-xs" data-testid="plan-add"><Plus size={11} /> Add instalment</button>}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Payments */}
        <div className="bg-[#FAF6EE] rounded-xl p-4 mb-4">
          <div className="font-medium text-[#5D2C8F] mb-2">Payments</div>
          {(inv.payments || []).length === 0 && <div className="text-xs text-[#6E6074] mb-2">No payments yet. Record a deposit or full payment below.</div>}
          {(inv.payments || []).map((p) => (
            <div key={p.id} className="flex items-center justify-between text-xs border-b border-[#E5E0D8]/60 py-1.5" data-testid={`payment-${p.id}`}>
              <div className="text-[#3A2A47]">{p.date} · {p.method.replace('_', ' ')} {p.note && <span className="text-[#6E6074]">· {p.note}</span>}</div>
              <div className="flex items-center gap-3">
                <span className="text-[#348469] font-medium">{money(p.amount)}</span>
                <button onClick={() => removePayment(p.id)} className="text-[#A685BD]"><Trash2 size={11} /></button>
              </div>
            </div>
          ))}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end text-xs">
            <Field label="Amount £">
              <input type="number" min="0" step="0.01" value={pmt.amount} onChange={(e) => setPmt({ ...pmt, amount: e.target.value })} placeholder="135.00" className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1.5" data-testid="pmt-amount" />
            </Field>
            <Field label="Date">
              <input type="date" value={pmt.date} onChange={(e) => setPmt({ ...pmt, date: e.target.value })} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1.5" data-testid="pmt-date" />
            </Field>
            <Field label="Method">
              <select value={pmt.method} onChange={(e) => setPmt({ ...pmt, method: e.target.value })} className="w-full bg-white border border-[#E5E0D8] rounded px-2 py-1.5" data-testid="pmt-method">
                <option value="bank_transfer">Bank transfer</option>
                <option value="stripe">Stripe (card)</option>
                <option value="cash">Cash</option>
              </select>
            </Field>
            <button
              onClick={addPayment}
              className="sac-btn-primary col-span-2 sm:col-span-1 w-full flex items-center justify-center gap-1 !py-2.5 text-xs leading-none"
              data-testid="pmt-add"
            >
              <Plus size={14} />
              <span className="whitespace-nowrap">Record &amp; email receipt</span>
            </button>
          </div>
        </div>

        <Field label="Notes (shown on invoice email)">
          <textarea rows={3} value={inv.notes || ""} onChange={(e) => setField("notes", e.target.value)} className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm" data-testid="invedit-notes" />
        </Field>

        <div className="flex items-center justify-end gap-3 mt-5">
          <button onClick={onClose} className="text-sm text-[#6E6074]">Cancel</button>
          <button onClick={save} className="sac-btn-primary" data-testid="invedit-save-btn">Save changes</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold = false, colour }) {
  return (
    <div className={`flex items-center justify-between py-0.5 ${bold ? "text-base font-bold" : ""}`} style={colour ? { color: colour } : {}}>
      <span className={!bold && !colour ? "text-[#7D6096]" : ""}>{label}</span>
      <span className={bold ? "text-[#5D2C8F]" : ""}>{value}</span>
    </div>
  );
}

function SortBtn({ label, k, sortBy, sortDir, onClick }) {
  const active = sortBy === k;
  return (
    <button onClick={() => onClick(k)} className={`inline-flex items-center gap-1 hover:text-[#A685BD] transition-colors ${active ? "underline decoration-[#A685BD] underline-offset-4" : ""}`} data-testid={`sort-${k}`}>
      {label} <ArrowUpDown size={11} className={active ? "opacity-100" : "opacity-30"} />
      {active && <span className="text-[10px] ml-0.5">{sortDir === "asc" ? "↑" : "↓"}</span>}
    </button>
  );
}

/**
 * Renders the Invoices desktop table honouring per-admin column prefs.
 * "number" is always first; "actions" always last.
 */
function InvoicesTableBody({ rows, colPrefs, sortBy, sortDir, toggleSort, handlers }) {
  const { send, markPaid, sendReminder, setEditing, remove } = handlers;
  const hidden = new Set(colPrefs?.hidden || []);
  const raw = (colPrefs?.order && colPrefs.order.length)
    ? colPrefs.order.filter((id) => INVOICE_COLUMNS_META.some((c) => c.id === id))
    : INVOICE_COLUMNS_META.map((c) => c.id);
  for (const c of INVOICE_COLUMNS_META) if (!raw.includes(c.id)) raw.push(c.id);
  const ordered = [
    "number",
    ...raw.filter((id) => id !== "number" && id !== "actions"),
    "actions",
  ];
  const visible = ordered.filter((id) => !hidden.has(id));

  const th = (id) => {
    switch (id) {
      case "number": return <SortBtn label="No." k="issued_at" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "student": return <SortBtn label="Student" k="student" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "term": return <SortBtn label="Term" k="term" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "total": return <SortBtn label="Total" k="total" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "paid": return "Paid";
      case "balance": return <SortBtn label="Balance" k="balance" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "due": return <SortBtn label="Due" k="due_date" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "reminder": return "Last reminder";
      case "status": return <SortBtn label="Status" k="status" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />;
      case "actions": return "";
      default: return id;
    }
  };

  const td = (id, i) => {
    const total = i.total ?? i.amount;
    const paid = i.amount_paid ?? 0;
    const balance = i.balance_due ?? Math.max(0, total - paid);
    const statusClass = i.status === 'paid'
      ? 'bg-[#8EC9C7]/20 text-[#5D2C8F]'
      : i.status === 'partial' ? 'bg-[#E8C37A]/20 text-[#8F6B1F]'
      : i.status === 'sent' ? 'bg-[#E8C37A]/15 text-[#8F6B1F]'
      : 'bg-[#E5E0D8] text-[#6E6074]';
    switch (id) {
      case "number": return <td className="px-4 py-3 text-[#5D2C8F] font-mono text-xs">{i.invoice_number}</td>;
      case "student": return <td className="px-4 py-3 text-[#5D2C8F]">{i.student_name}<div className="text-xs text-[#6E6074]">{i.student_email || <em>no email</em>}</div></td>;
      case "term": return <td className="px-4 py-3 text-[#6E6074] text-xs">{i.term || "—"}</td>;
      case "total": return <td className="px-4 py-3 text-[#5D2C8F]">{money(total)}{i.discount > 0 && <div className="text-[10px] text-[#C48A2B]">−{money(i.discount)} disc.</div>}</td>;
      case "paid": return <td className="px-4 py-3 text-[#348469]">{money(paid)}</td>;
      case "balance": return <td className="px-4 py-3 text-[#5D2C8F] font-semibold">{money(balance)}</td>;
      case "due": return <td className="px-4 py-3 text-[#6E6074] text-xs">{i.due_date || "—"}</td>;
      case "reminder": {
        const iso = i.reminder_last_sent_at;
        if (!iso) return <td className="px-4 py-3 text-[#6E6074] text-xs italic">Never</td>;
        const d = new Date(iso);
        const days = Math.floor((Date.now() - d.getTime()) / 86400000);
        const label = days === 0 ? "Today" : days === 1 ? "Yesterday" : `${days}d ago`;
        return (
          <td className="px-4 py-3 text-[#6E6074] text-xs">
            <div title={d.toLocaleString("en-GB")}>{d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</div>
            <div className="text-[10px] text-[#8A7C92]">{label}</div>
          </td>
        );
      }
      case "status": return <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${statusClass}`}>{i.status}</span></td>;
      case "actions":
        return (
          <td className="px-4 py-3 text-right whitespace-nowrap">
            {i.status !== 'paid' && <button onClick={() => send(i)} className="text-xs text-[#E8C37A] inline-flex items-center gap-1 mr-2" data-testid={`invoice-send-${i.id}`}><Send size={12} /> Send</button>}
            {i.status !== 'paid' && <button onClick={() => send(i, null, true)} title="Send a test copy to your inbox first" className="text-[10px] text-[#A685BD] inline-flex items-center gap-0.5 mr-3" data-testid={`invoice-send-test-${i.id}`}><TestTube2 size={10} /> test</button>}
            {i.status !== 'paid' && Number(i.balance_due ?? 0) > 0.01 && (
              <button
                onClick={() => sendReminder(i)}
                className="text-xs text-[#C48A2B] inline-flex items-center gap-1 mr-3"
                title="Send this student the same payment-reminder template the daily scheduler uses"
                data-testid={`invoice-send-reminder-${i.id}`}
              ><BellRing size={12} /> Remind</button>
            )}
            {i.status !== 'paid' && <button onClick={() => markPaid(i)} className="text-xs text-[#348469] inline-flex items-center gap-1 mr-3" data-testid={`invoice-mark-paid-${i.id}`}><CheckCircle2 size={12} /> Paid</button>}
            <button onClick={() => setEditing(i)} className="text-xs text-[#5D2C8F] inline-flex items-center gap-1 mr-3" data-testid={`invoice-edit-${i.id}`}><Pencil size={12} /> Edit</button>
            <button onClick={() => remove(i.id)} className="text-xs text-[#A685BD]" data-testid={`invoice-delete-${i.id}`}><Trash2 size={12} /></button>
          </td>
        );
      default: return <td />;
    }
  };

  return (
    <>
      <thead className="bg-[#FAF6EE] text-left text-[#5D2C8F]">
        <tr>{visible.map((id) => <th key={id} className="px-4 py-3" data-testid={`invoices-col-${id}`}>{th(id)}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((i) => (
          <tr key={i.id} className="border-t border-[#E5E0D8]">
            {visible.map((id) => <React.Fragment key={id}>{td(id, i)}</React.Fragment>)}
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={visible.length} className="px-4 py-10 text-center text-[#6E6074]">No invoices yet.</td></tr>}
      </tbody>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm text-[#5D2C8F] mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function InvoiceForm({ students, onClose, onCreate }) {
  const [f, setF] = useState({ student_id: students[0]?.id || "", amount: 405, method: "bank_transfer", notes: "" });
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="admin-surface w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="invoice-form-modal">
        <h3 className="font-display text-2xl text-[#5D2C8F] mb-4">Create invoice</h3>
        <p className="text-xs text-[#6E6074] mb-4">This creates a draft invoice with one line — open it to add deposits, starter packs, discounts etc.</p>
        <div className="space-y-3">
          <Field label="Student">
            <select value={f.student_id} onChange={(e) => setF({ ...f, student_id: e.target.value })} className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="invform-student">
              {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Starting amount (£)">
            <input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: parseFloat(e.target.value || 0) })} className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="invform-amount" />
          </Field>
          <Field label="Method">
            <select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} className="w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="invform-method">
              <option value="bank_transfer">Bank transfer (preferred)</option>
              <option value="stripe">Stripe (card)</option>
              <option value="cash">Cash</option>
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="text-sm text-[#6E6074]">Cancel</button>
          <button onClick={() => onCreate(f)} className="sac-btn-primary" data-testid="invform-save-btn">Create</button>
        </div>
      </div>
    </div>
  );
}
